import { createTileLayerComponent, updateGridLayer } from '@react-leaflet/core'
import axios from 'axios'
import L from 'leaflet'
import { memo } from 'react'

interface CogTileLayerProps {
  url: string
  onReady?: () => void
}

type CogTileLayerProxyProps = L.TileLayerOptions & CogTileLayerProps

class CogTileLayerProxyLeaflet extends L.TileLayer {
  isLoadingAsync: Promise<void> = new Promise(() => {})

  private readonly BASE_PROXY_URL = 'https://metaverse.cromai.com/cog'
  private readonly TILE_MATRIX_SET_ID = 'WebMercatorQuad'
  private url: string
  private CRS: string = ''
  private onReady = () => {}
  private readyFired = false

  constructor(
    { url, onReady }: CogTileLayerProps,
    options: L.TileLayerOptions,
  ) {
    super(url, {
      bounds: L.latLngBounds(L.latLng(0, 0), L.latLng(0, 0)),
      minZoom: 0,
      maxZoom: 23,
      ...options,
    })
    this.url = url
    this.onReady = () => {
      onReady?.()
    }
  }

  getTileUrl(coords: L.Coords): string {
    const { z, x, y } = coords
    const url = new URL(
      `${this.BASE_PROXY_URL}/tiles/${this.TILE_MATRIX_SET_ID}/${z}/${x}/${y}`,
    )
    url.searchParams.append('url', this.url)
    return url.toString()
  }

  onAdd(map: L.Map): this {
    super.onAdd(map)

    this.isLoadingAsync = this.init()
      .then(() => {
        this.once('load', () => this.fireReady())
        this.redraw()
      })
      .catch((error) => {
        console.error('Error initializing COG Tile Layer Proxy:', error)
      })

    return this
  }

  private async init(): Promise<void> {
    const { data } = await axios.get<{
      GEO: {
        BoundingBox: [number, number, number, number]
        MinZoom: number
        MaxZoom: number
        CRS: string
      }
    }>(`${this.BASE_PROXY_URL}/validate`, { params: { url: this.url } })

    this.CRS = data.GEO.CRS
    this.options.minNativeZoom = data.GEO.MinZoom
    this.options.maxNativeZoom = data.GEO.MaxZoom

    const bbox = data.GEO.BoundingBox
    this.options.bounds = L.latLngBounds(
      { lat: bbox[1], lng: bbox[0] },
      { lat: bbox[3], lng: bbox[2] },
    )
  }

  private fireReady() {
    if (this.readyFired) return
    this.readyFired = true
    this.onReady()
  }
}

export const CogTileLayerProxy = memo(
  createTileLayerComponent<L.TileLayer, CogTileLayerProxyProps>(
    ({ url, onReady, ...props }, context) => {
      return {
        instance: new CogTileLayerProxyLeaflet({ url, onReady }, props),
        context,
      }
    },
    (instance, props, prevProps) => {
      updateGridLayer(instance, props, prevProps)

      if (props.opacity !== prevProps.opacity && props.opacity !== undefined) {
        instance.setOpacity(props.opacity)
      }
    },
  ),
)
